import { AiModelsCatalogClient } from "../catalog/AiModelsCatalogClient.js";
import { AiToolsError, ModelProfileInputRejectedError } from "../errors.js";
import { isKnownProfileInput } from "../profiles/index.js";
import { loadOpenRouterRoutingEnv } from "../sync/openRouterRoutingEnv.js";
import type { ModelResolutionSuccess } from "../sync/modelNameResolver/types.js";
import { applyOpenRouterInvokeRouting } from "./applyOpenRouterInvokeRouting.js";
import { buildInvokeModelDiagnostics } from "./buildDiagnostics.js";
import { buildInvokeModelResolverOptions } from "./buildResolverOptions.js";
import { enrichModelResolutionError } from "./enrichModelResolutionError.js";
import { mapResolutionToRouterConfig } from "./mapResolutionToRouterConfig.js";
import { resolveOpenRouterApiKey } from "./preferOpenRouter.js";
import type {
  InvokeModelResolutionInput,
  InvokeModelResolutionOptions,
  InvokeModelResolutionResult,
} from "./types.js";

function passthroughResolution(model: string): ModelResolutionSuccess {
  return {
    found: true,
    modelId: model,
    record: null,
    routedViaOpenRouter: false,
    confidence: 1,
    resolvedVia: [],
    resolvedReason: "resolveModels disabled (passthrough)",
    normalisedInput: model,
  };
}

function defaultCatalog(options: InvokeModelResolutionOptions): AiModelsCatalogClient {
  return new AiModelsCatalogClient({
    bundledOnly: options.bundledOnly,
    resolverOptions: {
      routingEnv: options.routingEnv ?? loadOpenRouterRoutingEnv(),
    },
  });
}

/**
 * End-to-end invoke model resolution: catalog + profiles + vendor mapping + OR routing.
 */
export async function resolveInvokeModel(
  input: InvokeModelResolutionInput,
  options: InvokeModelResolutionOptions = {},
): Promise<InvokeModelResolutionResult> {
  const model = input.model?.trim();
  if (!model) {
    throw new AiToolsError("INVALID_INVOKE_INPUT", "Invoke model resolution requires a non-empty model string.");
  }

  const trimmedInput: InvokeModelResolutionInput = { ...input, model };

  if (options.modelsOnly && isKnownProfileInput(model)) {
    throw new ModelProfileInputRejectedError(model);
  }

  const routingEnv = options.routingEnv ?? loadOpenRouterRoutingEnv();
  const openRouterApiKey = resolveOpenRouterApiKey(options.openRouterApiKey);
  const resolveModels = options.resolveModels !== false;

  if (!resolveModels) {
    const router = applyOpenRouterInvokeRouting(
      {
        provider: input.provider ?? options.defaultProvider ?? "",
        model,
      },
      {
        routingEnv,
        preferOpenRouter: options.preferOpenRouter,
        openRouterApiKey,
      },
    );
    const resolution = passthroughResolution(model);
    return {
      router,
      resolution,
      diagnostics: buildInvokeModelDiagnostics(trimmedInput, resolution, router),
    };
  }

  const catalog = options.catalog ?? defaultCatalog(options);
  const resolverOptions = buildInvokeModelResolverOptions({
    routingEnv,
    preferOpenRouter: options.preferOpenRouter,
    catalogLane: options.catalogLane,
  });

  const resolution = await catalog.resolveModel(
    { provider: input.provider, model },
    resolverOptions,
  );

  if (!resolution.found) {
    throw enrichModelResolutionError(trimmedInput, resolution);
  }

  const router = applyOpenRouterInvokeRouting(
    mapResolutionToRouterConfig(resolution, trimmedInput, options.defaultProvider),
    {
      routingEnv,
      preferOpenRouter: options.preferOpenRouter,
      openRouterApiKey,
      resolution,
    },
  );

  return {
    router,
    resolution,
    diagnostics: buildInvokeModelDiagnostics(trimmedInput, resolution, router),
  };
}
